import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Grid, TextField } from '@mui/material';
import { getProfile, patchUpdateProfile } from 'redux/actions/SettingsAction';
import Loader from 'components/loader/Loader';
import { isSuperAdmin } from 'utils/jwtRole';
import profileFallback from 'assets/images/SubVendorLogo.svg';

// Super admin's own profile page for the Iter B2B corporate portal.
// Rendered INSIDE SuperAdminLayout (which supplies the sidebar) — this
// component is the page content only, no layout chrome.
//
// Data flow mirrors pages/setting/Setting.js exactly:
//   - store selector: state.auth.userProfile.data  (the GET /admin-api/v1/profile body)
//   - loader selector: state.auth.loading
//   - getProfile() dispatched on mount
//   - patchUpdateProfile(<body>) dispatched on save (PATCH /admin-api/v1/profile)
//
// patchUpdateProfile wraps whatever we pass as { userUpdateProfile } and the
// saga hands it straight to updateProfileApi as the PATCH body. Setting.js only
// ever passed a FormData (for the avatar file, keyed "avtar"); for editing text
// details we pass a plain object of the edited fields, which axios serialises
// as JSON. The saga owns success/error toasts and re-fetches the profile, so —
// like Setting.js — this component shows no toast of its own. Avatar stays
// read-only here (no upload endpoint is invented; that is the file-crop path in
// Setting.js).

// Fields the profile object carries that this page lets the super admin edit.
// Names match the canonical user shape used across the app (CreateTenant admin
// payload: firstName/lastName/mobile/countryCode/email; SettingsSaga reads
// countryCode/mobile/email off the same profile response).
const EDITABLE_FIELDS = ['firstName', 'lastName', 'email', 'mobile', 'countryCode'];

function SuperAdminProfile() {
  const dispatch = useDispatch();
  const userProfileData = useSelector((state) => state?.auth?.userProfile?.data);
  const loader = useSelector((state) => state?.auth?.loading);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    countryCode: '',
  });

  useEffect(() => {
    dispatch(getProfile());
  }, []);

  // Prefill from the store whenever the profile arrives / changes.
  useEffect(() => {
    if (userProfileData) {
      setForm({
        firstName: userProfileData?.firstName || '',
        lastName: userProfileData?.lastName || '',
        email: userProfileData?.email || '',
        mobile: userProfileData?.mobile || '',
        countryCode: userProfileData?.countryCode || '',
      });
    }
  }, [userProfileData]);

  // UI convenience gate only — the profile API's own role-99 check is the real
  // security wall. Mirrors CreateTenant.js.
  if (!isSuperAdmin()) {
    return (
      <div className="p-5">
        <div className="p-5 bg-white borderStyle">Only the super admin can view this profile.</div>
      </div>
    );
  }

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = () => {
    // Send only the editable fields the profile actually carries.
    const payload = EDITABLE_FIELDS.reduce((acc, field) => {
      acc[field] = form[field];
      return acc;
    }, {});
    dispatch(patchUpdateProfile(payload));
  };

  return (
    <div className="p-5">
      <div className="p-5 bg-white borderStyle">
        <h2 className="text-lg font-semibold mb-4">Profile</h2>
        {loader ? (
          <Loader />
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <img
                src={userProfileData?.avatar?.url || profileFallback}
                alt="profile"
                style={{
                  height: 80,
                  width: 80,
                  objectFit: 'cover',
                  borderRadius: '50%',
                  border: '1px solid #e0e0e0',
                  background: '#fff',
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="First Name"
                value={form.firstName}
                onChange={handleChange('firstName')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Last Name"
                value={form.lastName}
                onChange={handleChange('lastName')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Email ID"
                value={form.email}
                onChange={handleChange('email')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Country Code"
                value={form.countryCode}
                onChange={handleChange('countryCode')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Mobile Number"
                value={form.mobile}
                onChange={handleChange('mobile')}
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" onClick={handleSave}>
                Save
              </Button>
            </Grid>
          </Grid>
        )}
      </div>
    </div>
  );
}

export default SuperAdminProfile;
